/**
 * LocalComputerDetailPage — `/admin/local-computers/:id` route page.
 *
 * Self-contained workspace per Alexey's verbatim xxvii spec
 * (clarified 2026-05-23 → commit 4e2). Three sections — and nothing
 * else (no reset-keys, no bind block, no infra snippets, no
 * node-exporter probe, no separate maintenance section). The
 * commit-4e Dialog-wrapped modal body is gone; the commit-4e
 * MaintenanceCard is gone.
 *
 *   **Section 1 — Параметры**
 *   All operator-fillable inputs UPFRONT:
 *     - `deployment_key` (editable until DOCKER_RUNNING)
 *     - `display_name` (always editable)
 *     - `wg_ip` (editable until WG_HANDSHAKE_VERIFIED observed)
 *     - `device_number` (editable until REVERSE_SSH_LIVE observed)
 *   Jetson-originated values are read-only:
 *     - `eth0_mac`, `wg_pubkey`, `ssh_pubkey`, `hostname`,
 *       `hardware_model`, `reverse_ssh_port`.
 *   Persistent expandable Discovery panel at the bottom exposes
 *   `curl … /remote/discovery-script/ | sudo bash` — the one and only
 *   way to refresh jetson-originated values.
 *
 *   **Section 2 — Шаги настройки** (hidden when DOCKER_RUNNING)
 *   Renders `<SetupStageChecklist>` — each stage is exactly one of
 *   `success` / `failure` / `running` / `pending`. `running` ONLY
 *   when `lc.updated_at` is fresh (script genuinely moving), not
 *   "this is the next step". See SetupStageChecklist module
 *   docstring.
 *
 *   **Section 3 — Текущий шаг** (hidden when DOCKER_RUNNING)
 *   Mirrors Section 2's next-row state — running / pending / failure
 *   render with state-specific copy + action.
 *     - `running` → "Идёт <stage>..." (no action — wait).
 *     - `failure` → red error block + recovery hint pointing to the
 *       discovery rerun.
 *     - `pending` → if required operator params missing, point back
 *       to Section 1; else surface the setup curl (auto-issued on
 *       mount) with copy button.
 *
 * No inputs anywhere outside Section 1. No buttons that take input
 * (e.g. text fields, dialogs with form). Section 3's only action is
 * "copy the curl" — pure read.
 */
import { useEffect, useRef, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR, { mutate as swrMutate } from 'swr';
import { isAxiosError } from 'axios';
import ModelDeployPanel from './ModelDeployPanel';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  Cloud,
  CloudDownload,
  Copy,
  ExternalLink,
  Loader2,
  RefreshCw,
  Rocket,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useT } from '@/contexts/I18nContext';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import {
  bindLcZone,
  deleteLocalComputer,
  getDiscoveryScriptUrl,
  getLcZones,
  getLocalComputer,
  matchOrCreateOnGitlab,
  patchLocalComputer,
  pullFromGitlab,
  reissueEnrollmentToken,
  SERVICE_MODES,
  setLcSecret,
  SETUP_STAGES_ORDERED,
  unbindLcZone,
  type GitlabClash,
  type LcSecretName,
  type LocalComputer,
  type LcZonesResponse,
  type PullFromGitlabError,
  type ServiceMode,
  type SetupError,
  type SetupStage,
  type ZoneBindSummary,
} from '@/api/localComputers';
import { extractApiError } from '@/utils/apiError';
import SetupStageChecklist, {
  computeNextRowState,
} from '@/components/equipment/SetupStageChecklist';
import InlineEditableText from '@/components/common/InlineEditableText';
import { cn } from '@/lib/utils';

const detailSwrKey = (id: string) => `admin-local-computers:detail:${id}`;
const reloadList = () =>
  swrMutate(k => typeof k === 'string' && k.startsWith('admin-local-computers:'));

export default function LocalComputerDetailPage() {
  const { t } = useT();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // SWR poll cadence is adaptive (commit 4h polish): default 30s
  // (covers Section 2's 90s "running" window), but tightens to 5s
  // when the operator is actively waiting on the jetson — discovery
  // panel expanded, or Section 2 reports `running`. The faster tick
  // makes the "did my paste land?" feedback loop snappy without
  // hammering the BE during idle viewing.
  const [activelyWaiting, setActivelyWaiting] = useState(false);
  const swrKey = id ? detailSwrKey(id) : null;
  const { data: lc, error, isLoading, mutate: mutateLc } = useSWR<LocalComputer>(
    swrKey,
    () => getLocalComputer(id!).then(r => r.data),
    {
      refreshInterval: activelyWaiting ? 5_000 : 30_000,
      revalidateOnFocus: true,
    },
  );

  usePageTitle(
    t('admin.lc.detail.title'),
    lc?.display_name || lc?.hostname || lc?.deployment_key || (id ?? ''),
  );

  const back = () => navigate('/admin/local-computers');
  const refetch = async () => {
    await mutateLc();
    reloadList();
  };

  const isDockerRunning = lc?.setup_stage === 'DOCKER_RUNNING';
  // Required operator-fillable params for the setup-curl to run.
  // display_name is operator-friendly but not strictly required by the BE.
  const requiredParamsFilled = !!(
    lc?.deployment_key && lc?.wg_ip && lc?.device_number != null
  );

  const lastSetupError: SetupError | null =
    ((lc?.machine_facts as { last_setup_error?: SetupError } | undefined)
      ?.last_setup_error) ?? null;

  // Bump SWR cadence whenever the operator is actively waiting on
  // the jetson — Section 2 says `running` (script in flight). The
  // DiscoveryPanel expansion bumps via its own onActive callback
  // (passed in below).
  const { status: nextRowStatus } = lc
    ? computeNextRowState(lc, lastSetupError)
    : { status: 'pending' as const };
  useEffect(() => {
    if (nextRowStatus === 'running') setActivelyWaiting(true);
  }, [nextRowStatus]);

  const [setupCurl, setSetupCurl] = useState<string | null>(null);
  const [setupCurlError, setSetupCurlError] = useState<string | null>(null);
  const [setupCurlBusy, setSetupCurlBusy] = useState(false);
  // Track when the token underlying setupCurl was minted, so the copy
  // button can silently re-mint a fresh one if the operator's been
  // sitting on the page > 23h (4i polish, team-lead's call). Tokens
  // are 24h-valid on the BE; leave 1h slack so the pasted curl
  // doesn't fail mid-run if the operator's slow on the jetson side.
  const [setupCurlMintedAt, setSetupCurlMintedAt] = useState<number | null>(null);

  const issueSetupCurl = async () => {
    if (!lc) return;
    setSetupCurlBusy(true);
    setSetupCurlError(null);
    try {
      const resp = await reissueEnrollmentToken(lc.id);
      setSetupCurl(resp.data.setup_curl);
      setSetupCurlMintedAt(Date.now());
    } catch (e) {
      setSetupCurlError(extractApiError(e, t('error.general')));
    } finally {
      setSetupCurlBusy(false);
    }
  };

  // Auto-issue a setup-curl the first time we see required params
  // filled AND setup isn't already done. Section 3 needs a curl to
  // surface when its state is `pending`.
  useEffect(() => {
    if (!lc) return;
    if (isDockerRunning) return;
    if (!requiredParamsFilled) return;
    if (setupCurl || setupCurlBusy || setupCurlError) return;
    void issueSetupCurl();
    // Deps exclude `issueSetupCurl` (recreated each render — would
    // loop) + the local setup-curl state (handled by early-returns).
  }, [lc?.id, isDockerRunning, requiredParamsFilled, setupCurl, setupCurlBusy, setupCurlError]);

  return (
    <div className="container mx-auto max-w-3xl space-y-3 px-4 py-4">
      <div>
        <Button variant="ghost" size="sm" onClick={back}>
          <ArrowLeft className="size-3.5" />
          {t('admin.lc.detail.backToList')}
        </Button>
      </div>

      {error && !lc && (
        <Card>
          <CardContent className="p-0">
            <ServerStatusNotice variant="card" error={error} onRetry={() => mutateLc()} />
          </CardContent>
        </Card>
      )}

      {!lc && isLoading && (
        <Card>
          <CardContent className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('common.loading')}
          </CardContent>
        </Card>
      )}

      {lc && (
        <>
          <PageHeader lc={lc} t={t} onDeleted={back} />

          {/* Setup card sits ABOVE Params — it's the operator's
              attention target during onboarding, and it disappears
              once setup_stage = DOCKER_RUNNING (params card remains
              visible as the row's permanent identity surface). */}
          {!isDockerRunning && (
            <SetupCard
              lc={lc}
              lastSetupError={lastSetupError}
              requiredParamsFilled={requiredParamsFilled}
              setupCurl={setupCurl}
              setupCurlBusy={setupCurlBusy}
              setupCurlError={setupCurlError}
              setupCurlMintedAt={setupCurlMintedAt}
              onIssueCurl={issueSetupCurl}
              onChanged={refetch}
              t={t}
            />
          )}

          <ParamsCard lc={lc} t={t} onChanged={refetch} onDiscoveryActive={setActivelyWaiting} />

          {/* Model-deploy pull-cron (step 8): install command + cron-run history,
              per-point mirror of the Хост-инфраструктура cron-status view. */}
          {isDockerRunning && (
            <ModelDeployPanel
              lcId={lc.id}
              isConvertHost={!!lc.is_model_convert_host}
              isDesignated={!!lc.convert_host_designated}
            />
          )}
        </>
      )}
    </div>
  );
}

// ── Header ─────────────────────────────────────────────────────────

type T = (k: string, p?: Record<string, string | number>) => string;

function PageHeader({
  lc,
  t,
  onDeleted,
}: {
  lc: LocalComputer;
  t: T;
  onDeleted: () => void;
}) {
  const isComplete = lc.setup_stage === 'DOCKER_RUNNING' && !lc.is_archived;
  const variant: 'success' | 'muted' | 'warning' = isComplete
    ? 'success'
    : lc.is_archived
      ? 'muted'
      : 'warning';
  // No "N/9" numeric suffix on the badge (4i polish, team-lead's call):
  // SetupStageChecklist row positions carry the progress meaning.

  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const handleDelete = async () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteLocalComputer(lc.id);
      onDeleted();
    } catch (e) {
      setDeleteError(
        isAxiosError(e) ? extractApiError(e, t('error.general')) : t('error.general'),
      );
      setConfirming(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-start justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight">
          {lc.display_name || lc.hostname || t('admin.lc.unbound')}
        </h1>
        <div className="flex shrink-0 items-center gap-2">
          {confirming && !deleting && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirming(false)}
            >
              {t('common.cancel')}
            </Button>
          )}
          <Button
            variant={confirming ? 'destructive' : 'outline'}
            size="sm"
            onClick={handleDelete}
            disabled={deleting}
            aria-label={t('admin.lc.detail.maintenance.deleteAction')}
          >
            {deleting
              ? <Loader2 className="size-3.5 animate-spin" />
              : <Trash2 className="size-3.5" />}
            {confirming
              ? t('admin.lc.detail.maintenance.deleteConfirm')
              : t('admin.lc.detail.maintenance.deleteAction')}
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {lc.deployment_key && (
          <span className="font-mono">{lc.deployment_key}</span>
        )}
        <Badge variant={variant} className="font-normal">
          {t(`admin.lc.stage.${lc.setup_stage}`)}
        </Badge>
        {lc.is_archived && (
          <Badge variant="muted">{t('admin.lc.detail.field.stateArchived')}</Badge>
        )}
      </div>
      {deleteError && (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {deleteError}
        </div>
      )}
    </div>
  );
}

// ── Section 1: Params ──────────────────────────────────────────────

// LC field keys whose <Field> renders an inline `error` prop for
// PATCH failures. The bottom catch-all banner is suppressed for
// these to avoid double-display; everything else uses the banner.
const FIELDS_WITH_INLINE_ERROR = new Set<string>([
  'deployment_key',
  'wg_ip',
  'device_number',
]);

function ParamsCard({
  lc,
  t,
  onChanged,
  onDiscoveryActive,
}: {
  lc: LocalComputer;
  t: T;
  onChanged: () => Promise<void>;
  /** Propagated from the DiscoveryPanel: true while the panel is
   *  expanded (operator's actively running the curl, wants a tight
   *  SWR poll for fast feedback). */
  onDiscoveryActive: (active: boolean) => void;
}) {
  const [editError, setEditError] = useState<string | null>(null);
  // Per-field error tracking (bug #2 from Alexey's 2026-05-23 live
  // test): when PATCH errors, the field key gets stamped so the
  // matching <Field error={...}> renders red ring + inline text
  // next to the actual input the operator just edited — not
  // silently swallowed at the bottom of the card.
  const [editErrorField, setEditErrorField] = useState<keyof LocalComputer | null>(null);

  const save = async <K extends keyof LocalComputer>(
    field: K,
    transform: (raw: string) => LocalComputer[K] | null | undefined,
    raw: string,
  ) => {
    const value = transform(raw);
    if (value === undefined) return;
    setEditError(null);
    setEditErrorField(null);
    try {
      await patchLocalComputer(lc.id, { [field]: value } as never);
      await onChanged();
    } catch (e) {
      const msg = isAxiosError(e)
        ? extractApiError(e, t('error.general'))
        : t('error.general');
      setEditError(msg);
      setEditErrorField(field);
      throw e;
    }
  };

  const saveDeploymentKey = (raw: string) =>
    save('deployment_key', s => s.trim() || null, raw);
  // The 3 per-target spelling fields (task #93). Operator types or
  // accepts the backfilled default. Same lock policy as
  // deployment_key — once DOCKER_RUNNING, the values are referenced
  // by external systems (GitLab Environment, Prometheus DEPLOYMENT_NAME
  // label, BARE_METAL_VARIABLES_X group variable key) and shouldn't
  // drift.
  const saveGitlabEnvironmentName = (raw: string) =>
    save('gitlab_environment_name', s => s.trim() || null, raw);
  const savePrometheusDeploymentName = (raw: string) =>
    save('prometheus_deployment_name', s => s.trim() || null, raw);
  const saveBareMetalVariableName = (raw: string) =>
    save('bare_metal_variable_name', s => s.trim() || null, raw);
  const saveDisplayName = (raw: string) =>
    save('display_name', s => s.trim(), raw);
  // `hostname` is the jump-host name this box answers to in /etc/hosts
  // (`surf_iq.linza`), NOT the box's own OS hostname — every jetson in the
  // fleet ships as `linza-desktop`. ops/generate_hosts_from_wg.py reads this
  // column, so editing it here is what renames the box on the next nightly
  // /etc/hosts regeneration.
  const saveHostname = (raw: string) =>
    save('hostname', s => {
      const v = s.trim().toLowerCase();
      if (v && !/^[a-z0-9_][a-z0-9_.-]{0,126}[a-z0-9_]$/.test(v)) {
        setEditError(t('admin.lc.detail.field.hostnameInvalid'));
        return undefined;
      }
      return v;
    }, raw);
  const saveWgIp = (raw: string) =>
    save('wg_ip', s => {
      const v = s.trim();
      if (!v) return null;
      if (!/^10\.99\.0\.(?:[1-9]|[1-9]\d|1\d\d|2[0-4]\d|25[0-4])$/.test(v)) {
        setEditError(t('admin.lc.issueDialog.wgIpInvalid'));
        return undefined;
      }
      return v;
    }, raw);
  const saveDeviceNumber = (raw: string) =>
    save('device_number', s => {
      const v = s.trim();
      if (!v) return null;
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1 || n > 99) {
        setEditError(t('admin.lc.issueDialog.deviceNumberInvalid'));
        return undefined;
      }
      return n;
    }, raw);

  // Lock operator-fillable fields once observed reality has caught up.
  const stageIdx = SETUP_STAGES_ORDERED.indexOf(lc.setup_stage);
  const wgIpLocked =
    stageIdx >= SETUP_STAGES_ORDERED.indexOf('WG_HANDSHAKE_VERIFIED');
  const deviceNumberLocked =
    stageIdx >= SETUP_STAGES_ORDERED.indexOf('REVERSE_SSH_LIVE');
  const deploymentKeyLocked =
    stageIdx >= SETUP_STAGES_ORDERED.indexOf('DOCKER_RUNNING');

  return (
    <Card variant="section">
      <CardContent className="space-y-4">
        <SectionHeading>{t('admin.lc.detail.sections.params')}</SectionHeading>

        <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          {/* Operator-fillable */}
          <Field
            label={t('admin.lc.detail.field.deployment')}
            editable={!deploymentKeyLocked}
            value={deploymentKeyLocked ? (lc.deployment_key ?? '—') : undefined}
            mono={deploymentKeyLocked}
            isEmpty={!lc.deployment_key}
            emptyHint={t('admin.lc.detail.field.empty')}
            required={!deploymentKeyLocked}
            error={editErrorField === 'deployment_key' ? editError : null}
          >
            {!deploymentKeyLocked && (
              <InlineEditableText
                value={lc.deployment_key ?? ''}
                onSave={saveDeploymentKey}
                onError={setEditError}
                ariaLabel={t('admin.lc.detail.field.deployment')}
                fontSize="0.875rem"
                fontWeight={400}
              />
            )}
          </Field>
          {/* Per-target spelling fields (#93). Locked at the same
              stage as deployment_key — after DOCKER_RUNNING external
              systems hold references and edits would break them. */}
          <Field
            label={t('admin.lc.detail.field.gitlabEnvironmentName')}
            editable={!deploymentKeyLocked}
            value={deploymentKeyLocked ? (lc.gitlab_environment_name ?? '—') : undefined}
            mono={deploymentKeyLocked}
            isEmpty={!lc.gitlab_environment_name}
            emptyHint={t('admin.lc.detail.field.empty')}
          >
            {!deploymentKeyLocked && (
              <InlineEditableText
                value={lc.gitlab_environment_name ?? ''}
                onSave={saveGitlabEnvironmentName}
                onError={setEditError}
                ariaLabel={t('admin.lc.detail.field.gitlabEnvironmentName')}
                fontSize="0.875rem"
                fontWeight={400}
              />
            )}
          </Field>
          <Field
            label={t('admin.lc.detail.field.prometheusDeploymentName')}
            editable={!deploymentKeyLocked}
            value={deploymentKeyLocked ? (lc.prometheus_deployment_name ?? '—') : undefined}
            mono={deploymentKeyLocked}
            isEmpty={!lc.prometheus_deployment_name}
            emptyHint={t('admin.lc.detail.field.empty')}
          >
            {!deploymentKeyLocked && (
              <InlineEditableText
                value={lc.prometheus_deployment_name ?? ''}
                onSave={savePrometheusDeploymentName}
                onError={setEditError}
                ariaLabel={t('admin.lc.detail.field.prometheusDeploymentName')}
                fontSize="0.875rem"
                fontWeight={400}
              />
            )}
          </Field>
          <Field
            label={t('admin.lc.detail.field.bareMetalVariableName')}
            editable={!deploymentKeyLocked}
            value={deploymentKeyLocked ? (lc.bare_metal_variable_name ?? '—') : undefined}
            mono={deploymentKeyLocked}
            isEmpty={!lc.bare_metal_variable_name}
            emptyHint={t('admin.lc.detail.field.empty')}
          >
            {!deploymentKeyLocked && (
              <InlineEditableText
                value={lc.bare_metal_variable_name ?? ''}
                onSave={saveBareMetalVariableName}
                onError={setEditError}
                ariaLabel={t('admin.lc.detail.field.bareMetalVariableName')}
                fontSize="0.875rem"
                fontWeight={400}
              />
            )}
          </Field>
          <Field
            label={t('admin.lc.detail.field.displayName')}
            editable
            isEmpty={!lc.display_name}
            emptyHint={t('admin.lc.detail.field.empty')}
          >
            <InlineEditableText
              value={lc.display_name ?? ''}
              onSave={saveDisplayName}
              onError={setEditError}
              ariaLabel={t('admin.lc.detail.field.displayName')}
              fontSize="0.875rem"
              fontWeight={400}
            />
          </Field>
          <Field
            label={t('admin.lc.detail.field.wgIp')}
            editable={!wgIpLocked}
            value={wgIpLocked ? (lc.wg_ip ?? '—') : undefined}
            mono={wgIpLocked}
            isEmpty={!lc.wg_ip}
            emptyHint={t('admin.lc.detail.field.empty')}
            lockedHint={wgIpLocked ? t('admin.lc.detail.field.wgIpLocked') : undefined}
            required={!wgIpLocked}
            error={editErrorField === 'wg_ip' ? editError : null}
          >
            {!wgIpLocked && (
              <InlineEditableText
                value={lc.wg_ip ?? ''}
                onSave={saveWgIp}
                onError={setEditError}
                ariaLabel={t('admin.lc.detail.field.wgIp')}
                fontSize="0.875rem"
                fontWeight={400}
              />
            )}
          </Field>
          <Field
            label={t('admin.lc.detail.field.deviceNumber')}
            editable={!deviceNumberLocked}
            value={
              deviceNumberLocked
                ? lc.device_number != null
                  ? String(lc.device_number)
                  : '—'
                : undefined
            }
            mono={deviceNumberLocked}
            isEmpty={lc.device_number == null}
            emptyHint={t('admin.lc.detail.field.empty')}
            lockedHint={
              deviceNumberLocked
                ? t('admin.lc.detail.field.deviceNumberLocked')
                : undefined
            }
            required={!deviceNumberLocked}
            error={editErrorField === 'device_number' ? editError : null}
          >
            {!deviceNumberLocked && (
              <InlineEditableText
                value={lc.device_number != null ? String(lc.device_number) : ''}
                onSave={saveDeviceNumber}
                onError={setEditError}
                ariaLabel={t('admin.lc.detail.field.deviceNumber')}
                fontSize="0.875rem"
                fontWeight={400}
              />
            )}
          </Field>

          {/* Jetson-originated read-only — short scalars stay in the
              2-col grid; long-string fields (MAC, pubkeys) get
              col-span-2 + selectable mono blocks with copy buttons. */}
          <Field
            label={t('admin.lc.detail.field.sshPort')}
            value={lc.reverse_ssh_port != null ? String(lc.reverse_ssh_port) : '—'}
            mono
            muted={lc.reverse_ssh_port == null}
          />
          <Field
            label={t('admin.lc.detail.field.hostname')}
            editable
            isEmpty={!lc.hostname}
            emptyHint={t('admin.lc.detail.field.hostnameHint')}
          >
            <InlineEditableText
              value={lc.hostname ?? ''}
              onSave={saveHostname}
              onError={setEditError}
              ariaLabel={t('admin.lc.detail.field.hostname')}
              placeholder={t('admin.lc.detail.field.hostnameHint')}
              fontSize="0.875rem"
              fontWeight={400}
            />
          </Field>
          <Field
            label={t('admin.lc.detail.field.hardware')}
            value={lc.hardware_model}
          />
          <CopyableField label={t('admin.lc.detail.field.eth0Mac')} value={lc.eth0_mac} t={t} />
          <CopyableField label={t('admin.lc.detail.field.wgPubkey')} value={lc.wg_pubkey} t={t} />
          <CopyableField label={t('admin.lc.detail.field.sshPubkey')} value={lc.ssh_pubkey} t={t} />
        </div>

        {/* Catch-all banner for PATCH errors on fields that don't have
            their own per-field `error` slot. The 3 required-fields
            (deployment_key, wg_ip, device_number) DO render the
            error inline via <Field error={...}> — for them we skip
            the banner. All other fields (display_name, the GitLab
            config fields, secrets, etc.) fall back to this banner
            so PATCH errors are still visible. */}
        {editError && !FIELDS_WITH_INLINE_ERROR.has(editErrorField as string) && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {editError}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          {t('admin.lc.detail.jetsonOriginatedHint')}
        </p>

        {/* Discovery panel sits AFTER the jetson-originated params
            block and BEFORE the GitLab config subsection — its scope
            is explicitly "refresh the jetson-side fields above"
            (eth0_mac, wg_pubkey, ssh_pubkey). Putting it above
            GitLab config keeps that scoping visually obvious;
            pre-rearrangement it was right under GitLab config which
            implied an association that doesn't exist. */}
        <DiscoveryPanel lcId={lc.id} t={t} onActive={onDiscoveryActive} />

        <GitlabConfigSection lc={lc} t={t} onChanged={onChanged} />

        <ZoneBindSection lc={lc} t={t} />
      </CardContent>
    </Card>
  );
}

// ── GitLab config (#96 / #97) ─────────────────────────────────────
//
// Subsection inside ParamsCard (sibling to DiscoveryPanel at the
// bottom). Renders the 16 LC fields that map to the GitLab group
// variables (BARE_METAL_VARIABLES_<env> + APP_ENV_<env>). Operator
// fills the form once on a NEW LC + clicks "Create on GitLab" →
// done. Fields lock after a successful create; subsequent edits to
// the GitLab values happen via the GitLab UI directly (the LC
// detail page doesn't round-trip).
//
// Scope per team-lead 2026-05-23 (simplified from the original
// spec): NO Pull, NO drift banner, NO mirror-vs-GitLab state
// machine. EXISTING 11 LCs aren't touched by this feature — they
// kept their manual GitLab setup; the new fields render empty for
// them and the create button is the only path forward.
//
// Per gitlab-pipeline's spec:
//   - push-to-gitlab/ is synchronous (~10-15s) and advances
//     setup_stage REVERSE_SSH_LIVE → GITLAB_REGISTERED inline.
//     Response carries `advanced_stage: bool`.
//   - Secrets (metal_machine_password, sstpc_password) go through a
//     separate set-secret/ endpoint that updates only the *_set bool
//     flag locally. The plaintext lives only on GitLab.

interface GitlabConfigFormState {
  plugins_list: string;
  modules_list: string;
  service_mode: ServiceMode;
  service_port: string; // string for empty-state; convert at submit
  service_name: string;
  metal_machine_host: string;
  metal_machine_user: string;
  ssh_port: string;
  need_sstpc_vpn: boolean;
  router_domain: string;
  sstpc_user: string;
  rebuild_triton: boolean;
  proxy_url: string;
  telegram_data_save_path: string;
  // ── Phase B: advanced runtime config (defaults from BE migration). ──
  batch_size: string;
  conn_health_checks: boolean;
  debug: boolean;
  default_triton_url: string;
  enable_s3: boolean;
  frame_workload_enabled: boolean;
  gstreamer_pipeline: string;
  model_empty_shelf_name: string;
  queue_threads_num: string;
  redis_url: string;
  s3_access_key: string;
  s3_bucket: string;
  yolo_detectable_class_ids: string;
  yolo_model_name: string;
  yolo_model_version: string;
}

function _formStateFromLc(lc: LocalComputer): GitlabConfigFormState {
  return {
    plugins_list: lc.plugins_list ?? '',
    modules_list: lc.modules_list ?? '',
    service_mode: (lc.service_mode ?? 'PROD') as ServiceMode,
    service_port: lc.service_port != null ? String(lc.service_port) : '',
    service_name: lc.service_name ?? '',
    metal_machine_host: lc.metal_machine_host ?? '',
    metal_machine_user: lc.metal_machine_user ?? '',
    ssh_port: lc.ssh_port != null ? String(lc.ssh_port) : '',
    need_sstpc_vpn: !!lc.need_sstpc_vpn,
    router_domain: lc.router_domain ?? '',
    sstpc_user: lc.sstpc_user ?? '',
    rebuild_triton: !!lc.rebuild_triton,
    proxy_url: lc.proxy_url ?? '',
    telegram_data_save_path: lc.telegram_data_save_path ?? '',
    // Phase B fields. Numeric fields kept as strings here so empty
    // state shows blank — the patch helper handles string→number.
    batch_size: lc.batch_size != null ? String(lc.batch_size) : '',
    conn_health_checks: !!lc.conn_health_checks,
    debug: !!lc.debug,
    default_triton_url: lc.default_triton_url ?? '',
    enable_s3: !!lc.enable_s3,
    frame_workload_enabled: !!lc.frame_workload_enabled,
    gstreamer_pipeline: lc.gstreamer_pipeline ?? '',
    model_empty_shelf_name: lc.model_empty_shelf_name ?? '',
    queue_threads_num: lc.queue_threads_num != null ? String(lc.queue_threads_num) : '',
    redis_url: lc.redis_url ?? '',
    s3_access_key: lc.s3_access_key ?? '',
    s3_bucket: lc.s3_bucket ?? '',
    yolo_detectable_class_ids: lc.yolo_detectable_class_ids ?? '',
    yolo_model_name: lc.yolo_model_name ?? '',
    yolo_model_version: lc.yolo_model_version ?? '',
  };
}

function GitlabConfigSection({
  lc,
  t,
  onChanged,
}: {
  lc: LocalComputer;
  t: T;
  onChanged: () => Promise<void>;
}) {
  // Form lock signal — gitlab-pipeline's #96 BE marks `gitlab_locked_at`
  // non-null on either `matched` or `created` outcome from the
  // /match-or-create-on-gitlab/ endpoint. Unlock path:
  // `PATCH {gitlab_locked_at: null}` (gated behind UnlockGitlabDialog).
  const everCreated = lc.gitlab_locked_at != null;
  const createdAt = lc.gitlab_created_at;

  // Local form state, seeded from the LC. Re-seed when the LC
  // updates (e.g. the operator just inline-saved a field via PATCH).
  const [form, setForm] = useState<GitlabConfigFormState>(() => _formStateFromLc(lc));
  const [seedFromUpdatedAt, setSeedFromUpdatedAt] = useState(lc.updated_at);
  if (lc.updated_at !== seedFromUpdatedAt) {
    setForm(_formStateFromLc(lc));
    setSeedFromUpdatedAt(lc.updated_at);
  }

  // Patch-level error surfaces here (e.g. PATCH /admin/<id>/ failed
  // mid-edit). The CREATE flow lives in Section 3 now (team-lead
  // 2026-05-23 redesign) — its errors surface there.
  const [patchError, setPatchError] = useState<string | null>(null);
  // "Show advanced" gate per team-lead 2026-05-23. Default collapsed
  // so the operator sees ~10 per-LC fields instead of ~36. Toggle
  // reveals the 15 advanced runtime fields + 4 advanced secrets.
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Pull-from-GitLab state (bug #3 from Alexey's 2026-05-24 surf_21
  // test). Renders only when `everCreated` — operator clicks to
  // refresh the LC mirror from GitLab after editing values via the
  // GitLab UI directly. Pure mirror refresh: does NOT touch
  // setup_stage / gitlab_locked_at.
  const [pulling, setPulling] = useState(false);
  const [pullToast, setPullToast] = useState<
    { kind: 'success'; count: number; fields: string[] }
    | { kind: 'error'; message: string }
    | null
  >(null);

  // Secret setting flow. State is which secret is being edited
  // (`null` = no inline secret form is open) + the typed value.
  const [secretEditing, setSecretEditing] = useState<LcSecretName | null>(null);
  const [secretValue, setSecretValue] = useState('');
  const [secretSubmitting, setSecretSubmitting] = useState(false);
  const [secretError, setSecretError] = useState<string | null>(null);

  const updateField = <K extends keyof GitlabConfigFormState>(
    key: K, value: GitlabConfigFormState[K],
  ) => setForm(prev => ({ ...prev, [key]: value }));

  // Save a single non-secret field via the existing PATCH path. This
  // is what writes operator edits to the BE before they hit Push;
  // we patch on blur (per-field) instead of accumulating to a "save"
  // button — matches the rest of the page's inline-edit pattern.
  const patchField = async <K extends keyof GitlabConfigFormState>(
    key: K, raw: GitlabConfigFormState[K],
  ) => {
    // Convert string→number for the int fields. service_port +
    // batch_size + queue_threads_num + ssh_port are all integers
    // on the BE; the FE keeps them as strings in form state for
    // empty-input UX.
    let value: unknown = raw;
    const intFields: Array<keyof GitlabConfigFormState> = [
      'service_port', 'ssh_port', 'batch_size', 'queue_threads_num',
    ];
    if (intFields.includes(key)) {
      const trimmed = String(raw).trim();
      value = trimmed === '' ? null : Number(trimmed);
      if (value !== null && (!Number.isInteger(value) || (value as number) < 1)) return;
    }
    try {
      await patchLocalComputer(lc.id, { [key]: value } as never);
      await onChanged();
      setPatchError(null);
    } catch (e) {
      setPatchError(isAxiosError(e) ? extractApiError(e, t('error.general')) : t('error.general'));
    }
  };

  const submitSecret = async () => {
    if (!secretEditing || !secretValue) return;
    setSecretSubmitting(true);
    setSecretError(null);
    try {
      await setLcSecret(lc.id, secretEditing, secretValue);
      await onChanged();
      setSecretEditing(null);
      setSecretValue('');
    } catch (e) {
      setSecretError(isAxiosError(e) ? extractApiError(e, t('error.general')) : t('error.general'));
    } finally {
      setSecretSubmitting(false);
    }
  };

  // Pull-from-GitLab handler (#101 / Alexey bug #3, 2026-05-24).
  // POSTs to /pull-from-gitlab/ — BE re-reads the env's
  // BARE_METAL_VARIABLES + APP_ENV + DB_ENV and overwrites the LC
  // mirror columns. Returns a per-field changed list so we can
  // surface "уже синхронизировано" vs "обновлено N полей" in the
  // toast. Auto-dismisses after 5s. Errors land in a separate
  // toast variant — we don't reuse `patchError` because that lane
  // is dedicated to inline-edit PATCHes.
  const doPull = async () => {
    if (pulling) return;
    setPulling(true);
    setPullToast(null);
    try {
      const { data } = await pullFromGitlab(lc.id);
      await onChanged();
      setPullToast({
        kind: 'success',
        count: data.pulled_field_count,
        fields: data.changed_fields ?? [],
      });
    } catch (e) {
      const msg = isAxiosError<PullFromGitlabError>(e)
        ? extractApiError(e, t('error.general'))
        : t('error.general');
      setPullToast({ kind: 'error', message: msg });
    } finally {
      setPulling(false);
      // Auto-dismiss after 5s.
      window.setTimeout(() => {
        setPullToast(curr => (curr ? null : curr));
      }, 5000);
    }
  };

  // Section 1 doesn't drive the create action anymore — that moved
  // to Section 3's GITLAB_REGISTERED step state machine (team-lead
  // 2026-05-23 redesign). The form here is purely the params surface;
  // it locks read-only once the BE reports the LC was matched-or-
  // created against an existing GitLab environment (signalled by
  // `last_pushed_to_gitlab_at` being populated).
  const readOnly = everCreated;

  return (
    <div className="space-y-4 border-t border-border/60 pt-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Cloud className="size-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold tracking-tight">
            {t('admin.lc.detail.gitlabConfig.title')}
          </h3>
          {everCreated && (
            <Badge variant="muted" className="gap-1 text-[10px] text-emerald-700 dark:text-emerald-300">
              <Check className="size-3" />
              {t('admin.lc.detail.gitlabConfig.created')}
            </Badge>
          )}
          {everCreated && createdAt && (
            <span className="text-[11px] text-muted-foreground">
              {t('admin.lc.detail.gitlabConfig.createdAt', {
                when: formatRelativeTime(createdAt, t),
              })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Pull-from-GitLab button (#101). Only renders post-create:
              before lock, the LC mirror IS the source of truth, so
              pulling would just overwrite operator edits with
              themselves. */}
          {everCreated && (
            <button
              type="button"
              onClick={doPull}
              disabled={pulling}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
            >
              <CloudDownload className="size-3.5" />
              {t(pulling
                ? 'admin.lc.detail.gitlabConfig.pull.pulling'
                : 'admin.lc.detail.gitlabConfig.pull.button')}
            </button>
          )}
          {/* "Show advanced" toggle moved here from below the header —
              now that Section 1 has no Create action, the toggle is
              the only header-right affordance. */}
          <button
            type="button"
            onClick={() => setShowAdvanced(v => !v)}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            {showAdvanced ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
            {t(showAdvanced
              ? 'admin.lc.detail.gitlabConfig.advanced.hide'
              : 'admin.lc.detail.gitlabConfig.advanced.show')}
          </button>
        </div>
      </div>

      {/* PATCH-level error banner (single-field save failed). The
          create flow's errors surface in Section 3 now. */}
      {patchError && (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {patchError}
        </div>
      )}

      {/* Pull-from-GitLab outcome toast. Auto-dismisses after 5s
          inside doPull's setTimeout. Two variants:
          - success/0 → "уже синхронизировано"
          - success/N → "обновлено N полей" + comma-separated list
          - error → red banner with the BE detail */}
      {pullToast && pullToast.kind === 'success' && (
        <div className="rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">
          {pullToast.count === 0
            ? t('admin.lc.detail.gitlabConfig.pull.upToDate')
            : t('admin.lc.detail.gitlabConfig.pull.success', {
                count: String(pullToast.count),
                fields: pullToast.fields.join(', '),
              })}
        </div>
      )}
      {pullToast && pullToast.kind === 'error' && (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {pullToast.message}
        </div>
      )}

      {/* 3-group form per team-lead 2026-05-23: SSH access /
          Runtime config / Secrets. Each group renders a small
          sub-heading + a 2-col grid. Advanced fields gated by
          showAdvanced render at the END of their group. */}

      <GitlabFormGroup title={t('admin.lc.detail.gitlabConfig.group.ssh')}>
        <GitlabField label={t('admin.lc.detail.gitlabConfig.field.metal_machine_host')}>
          <Input
            value={form.metal_machine_host}
            onChange={e => updateField('metal_machine_host', e.target.value)}
            onBlur={() => patchField('metal_machine_host', form.metal_machine_host)}
            disabled={readOnly}
          />
        </GitlabField>
        <GitlabField label={t('admin.lc.detail.gitlabConfig.field.metal_machine_user')}>
          <Input
            value={form.metal_machine_user}
            onChange={e => updateField('metal_machine_user', e.target.value)}
            onBlur={() => patchField('metal_machine_user', form.metal_machine_user)}
            disabled={readOnly}
          />
        </GitlabField>
        <GitlabField label={t('admin.lc.detail.gitlabConfig.field.ssh_port')}>
          <Input
            type="number"
            value={form.ssh_port}
            onChange={e => updateField('ssh_port', e.target.value)}
            onBlur={() => patchField('ssh_port', form.ssh_port)}
            disabled={readOnly}
          />
        </GitlabField>
        <GitlabField label={t('admin.lc.detail.gitlabConfig.field.need_sstpc_vpn')}>
          <SwitchRow
            checked={form.need_sstpc_vpn}
            onCheckedChange={v => {
              updateField('need_sstpc_vpn', v);
              patchField('need_sstpc_vpn', v);
            }}
            disabled={readOnly}
            label={t(`admin.lc.detail.gitlabConfig.bool.${form.need_sstpc_vpn ? 'on' : 'off'}`)}
          />
        </GitlabField>
        {form.need_sstpc_vpn && (
          <>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.router_domain')}>
              <Input
                value={form.router_domain}
                onChange={e => updateField('router_domain', e.target.value)}
                onBlur={() => patchField('router_domain', form.router_domain)}
                disabled={readOnly}
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.sstpc_user')}>
              <Input
                value={form.sstpc_user}
                onChange={e => updateField('sstpc_user', e.target.value)}
                onBlur={() => patchField('sstpc_user', form.sstpc_user)}
                disabled={readOnly}
              />
            </GitlabField>
          </>
        )}
      </GitlabFormGroup>

      <GitlabFormGroup title={t('admin.lc.detail.gitlabConfig.group.runtime')}>
        <GitlabField
          label={t('admin.lc.detail.gitlabConfig.field.plugins_list')}
          hint={t('admin.lc.detail.gitlabConfig.field.plugins_list.hint')}
        >
          <Input
            value={form.plugins_list}
            onChange={e => updateField('plugins_list', e.target.value)}
            onBlur={() => patchField('plugins_list', form.plugins_list)}
            disabled={readOnly}
            placeholder="table_control,empty_shelf"
          />
        </GitlabField>
        <GitlabField label={t('admin.lc.detail.gitlabConfig.field.modules_list')}>
          <Input
            value={form.modules_list}
            onChange={e => updateField('modules_list', e.target.value)}
            onBlur={() => patchField('modules_list', form.modules_list)}
            disabled={readOnly}
          />
        </GitlabField>
        <GitlabField label={t('admin.lc.detail.gitlabConfig.field.service_mode')}>
          <Select
            value={form.service_mode}
            onChange={e => {
              const v = e.target.value as ServiceMode;
              updateField('service_mode', v);
              patchField('service_mode', v);
            }}
            disabled={readOnly}
          >
            {SERVICE_MODES.map(m => <option key={m} value={m}>{m}</option>)}
          </Select>
        </GitlabField>
        <GitlabField label={t('admin.lc.detail.gitlabConfig.field.service_port')}>
          <Input
            type="number"
            value={form.service_port}
            onChange={e => updateField('service_port', e.target.value)}
            onBlur={() => patchField('service_port', form.service_port)}
            disabled={readOnly}
            placeholder="—"
          />
        </GitlabField>
        <GitlabField label={t('admin.lc.detail.gitlabConfig.field.service_name')}>
          <Input
            value={form.service_name}
            onChange={e => updateField('service_name', e.target.value)}
            onBlur={() => patchField('service_name', form.service_name)}
            disabled={readOnly}
          />
        </GitlabField>
        <GitlabField label={t('admin.lc.detail.gitlabConfig.field.proxy_url')}>
          <Input
            value={form.proxy_url}
            onChange={e => updateField('proxy_url', e.target.value)}
            onBlur={() => patchField('proxy_url', form.proxy_url)}
            disabled={readOnly}
            placeholder="—"
          />
        </GitlabField>
        <GitlabField label={t('admin.lc.detail.gitlabConfig.field.telegram_data_save_path')}>
          <Input
            value={form.telegram_data_save_path}
            onChange={e => updateField('telegram_data_save_path', e.target.value)}
            onBlur={() => patchField('telegram_data_save_path', form.telegram_data_save_path)}
            disabled={readOnly}
            placeholder="telegram/surf-coffee-…"
          />
        </GitlabField>
        <GitlabField label={t('admin.lc.detail.gitlabConfig.field.rebuild_triton')}>
          <SwitchRow
            checked={form.rebuild_triton}
            onCheckedChange={v => {
              updateField('rebuild_triton', v);
              patchField('rebuild_triton', v);
            }}
            disabled={readOnly}
            label={t(`admin.lc.detail.gitlabConfig.bool.${form.rebuild_triton ? 'on' : 'off'}`)}
          />
        </GitlabField>
        {showAdvanced && (
          <>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.batch_size')}>
              <Input
                type="number"
                value={form.batch_size}
                onChange={e => updateField('batch_size', e.target.value)}
                onBlur={() => patchField('batch_size', form.batch_size)}
                disabled={readOnly}
                placeholder="8"
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.queue_threads_num')}>
              <Input
                type="number"
                value={form.queue_threads_num}
                onChange={e => updateField('queue_threads_num', e.target.value)}
                onBlur={() => patchField('queue_threads_num', form.queue_threads_num)}
                disabled={readOnly}
                placeholder="4"
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.default_triton_url')}>
              <Input
                value={form.default_triton_url}
                onChange={e => updateField('default_triton_url', e.target.value)}
                onBlur={() => patchField('default_triton_url', form.default_triton_url)}
                disabled={readOnly}
                placeholder="triton-server:8325"
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.redis_url')}>
              <Input
                value={form.redis_url}
                onChange={e => updateField('redis_url', e.target.value)}
                onBlur={() => patchField('redis_url', form.redis_url)}
                disabled={readOnly}
                placeholder="redis://redis:6379"
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.gstreamer_pipeline')}>
              <Input
                value={form.gstreamer_pipeline}
                onChange={e => updateField('gstreamer_pipeline', e.target.value)}
                onBlur={() => patchField('gstreamer_pipeline', form.gstreamer_pipeline)}
                disabled={readOnly}
                placeholder="—"
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.model_empty_shelf_name')}>
              <Input
                value={form.model_empty_shelf_name}
                onChange={e => updateField('model_empty_shelf_name', e.target.value)}
                onBlur={() => patchField('model_empty_shelf_name', form.model_empty_shelf_name)}
                disabled={readOnly}
                placeholder="yolov5-freezer"
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.yolo_model_name')}>
              <Input
                value={form.yolo_model_name}
                onChange={e => updateField('yolo_model_name', e.target.value)}
                onBlur={() => patchField('yolo_model_name', form.yolo_model_name)}
                disabled={readOnly}
                placeholder="yolov5"
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.yolo_model_version')}>
              <Input
                value={form.yolo_model_version}
                onChange={e => updateField('yolo_model_version', e.target.value)}
                onBlur={() => patchField('yolo_model_version', form.yolo_model_version)}
                disabled={readOnly}
                placeholder="1"
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.yolo_detectable_class_ids')}>
              <Input
                value={form.yolo_detectable_class_ids}
                onChange={e => updateField('yolo_detectable_class_ids', e.target.value)}
                onBlur={() => patchField('yolo_detectable_class_ids', form.yolo_detectable_class_ids)}
                disabled={readOnly}
                placeholder="0"
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.enable_s3')}>
              <SwitchRow
                checked={form.enable_s3}
                onCheckedChange={v => {
                  updateField('enable_s3', v);
                  patchField('enable_s3', v);
                }}
                disabled={readOnly}
                label={t(`admin.lc.detail.gitlabConfig.bool.${form.enable_s3 ? 'on' : 'off'}`)}
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.s3_bucket')}>
              <Input
                value={form.s3_bucket}
                onChange={e => updateField('s3_bucket', e.target.value)}
                onBlur={() => patchField('s3_bucket', form.s3_bucket)}
                disabled={readOnly || !form.enable_s3}
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.s3_access_key')}>
              <Input
                value={form.s3_access_key}
                onChange={e => updateField('s3_access_key', e.target.value)}
                onBlur={() => patchField('s3_access_key', form.s3_access_key)}
                disabled={readOnly || !form.enable_s3}
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.frame_workload_enabled')}>
              <SwitchRow
                checked={form.frame_workload_enabled}
                onCheckedChange={v => {
                  updateField('frame_workload_enabled', v);
                  patchField('frame_workload_enabled', v);
                }}
                disabled={readOnly}
                label={t(`admin.lc.detail.gitlabConfig.bool.${form.frame_workload_enabled ? 'on' : 'off'}`)}
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.conn_health_checks')}>
              <SwitchRow
                checked={form.conn_health_checks}
                onCheckedChange={v => {
                  updateField('conn_health_checks', v);
                  patchField('conn_health_checks', v);
                }}
                disabled={readOnly}
                label={t(`admin.lc.detail.gitlabConfig.bool.${form.conn_health_checks ? 'on' : 'off'}`)}
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.debug')}>
              <SwitchRow
                checked={form.debug}
                onCheckedChange={v => {
                  updateField('debug', v);
                  patchField('debug', v);
                }}
                disabled={readOnly}
                label={t(`admin.lc.detail.gitlabConfig.bool.${form.debug ? 'on' : 'off'}`)}
              />
            </GitlabField>
          </>
        )}
      </GitlabFormGroup>

      <GitlabFormGroup
        title={t('admin.lc.detail.gitlabConfig.group.secrets')}
        hint={t('admin.lc.detail.gitlabConfig.field.password.hint')}
      >
        <GitlabField label={t('admin.lc.detail.gitlabConfig.field.metal_machine_password')}>
          <SecretField
            isSet={lc.metal_machine_password_set}
            isEditing={secretEditing === 'metal_machine_password'}
            value={secretEditing === 'metal_machine_password' ? secretValue : ''}
            submitting={secretSubmitting}
            error={secretEditing === 'metal_machine_password' ? secretError : null}
            disabled={readOnly}
            onStartEdit={() => {
              setSecretEditing('metal_machine_password');
              setSecretValue('');
              setSecretError(null);
            }}
            onCancelEdit={() => {
              setSecretEditing(null);
              setSecretValue('');
            }}
            onChange={v => setSecretValue(v)}
            onSubmit={submitSecret}
            t={t}
          />
        </GitlabField>
        {form.need_sstpc_vpn && (
          <GitlabField label={t('admin.lc.detail.gitlabConfig.field.sstpc_password')}>
            <SecretField
              isSet={lc.sstpc_password_set}
              isEditing={secretEditing === 'sstpc_password'}
              value={secretEditing === 'sstpc_password' ? secretValue : ''}
              submitting={secretSubmitting}
              error={secretEditing === 'sstpc_password' ? secretError : null}
              disabled={readOnly}
              onStartEdit={() => {
                setSecretEditing('sstpc_password');
                setSecretValue('');
                setSecretError(null);
              }}
              onCancelEdit={() => {
                setSecretEditing(null);
                setSecretValue('');
              }}
              onChange={v => setSecretValue(v)}
              onSubmit={submitSecret}
              t={t}
            />
          </GitlabField>
        )}
        {/* postgres_password is always-visible (LC connects to the
            central deployments-server postgres over the WG mesh —
            password is per-LC even though host/port/etc are shared
            template constants in db.env). */}
        <GitlabField label={t('admin.lc.detail.gitlabConfig.field.postgres_password')}>
          <SecretField
            isSet={lc.postgres_password_set}
            isEditing={secretEditing === 'postgres_password'}
            value={secretEditing === 'postgres_password' ? secretValue : ''}
            submitting={secretSubmitting}
            error={secretEditing === 'postgres_password' ? secretError : null}
            disabled={readOnly}
            onStartEdit={() => {
              setSecretEditing('postgres_password');
              setSecretValue('');
              setSecretError(null);
            }}
            onCancelEdit={() => {
              setSecretEditing(null);
              setSecretValue('');
            }}
            onChange={v => setSecretValue(v)}
            onSubmit={submitSecret}
            t={t}
          />
        </GitlabField>
        {/* Advanced secrets — 4 application-stack secrets that most
            operators won't ever touch on the LC form (they're either
            ops-secrets like django_salt + sentry_dsn or S3 creds
            paired with the public access_key in the runtime group). */}
        {showAdvanced && (
          <>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.django_salt')}>
              <SecretField
                isSet={lc.django_salt_set}
                isEditing={secretEditing === 'django_salt'}
                value={secretEditing === 'django_salt' ? secretValue : ''}
                submitting={secretSubmitting}
                error={secretEditing === 'django_salt' ? secretError : null}
                disabled={readOnly}
                onStartEdit={() => {
                  setSecretEditing('django_salt');
                  setSecretValue('');
                  setSecretError(null);
                }}
                onCancelEdit={() => {
                  setSecretEditing(null);
                  setSecretValue('');
                }}
                onChange={v => setSecretValue(v)}
                onSubmit={submitSecret}
                t={t}
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.s3_secret_access_key')}>
              <SecretField
                isSet={lc.s3_secret_access_key_set}
                isEditing={secretEditing === 's3_secret_access_key'}
                value={secretEditing === 's3_secret_access_key' ? secretValue : ''}
                submitting={secretSubmitting}
                error={secretEditing === 's3_secret_access_key' ? secretError : null}
                disabled={readOnly}
                onStartEdit={() => {
                  setSecretEditing('s3_secret_access_key');
                  setSecretValue('');
                  setSecretError(null);
                }}
                onCancelEdit={() => {
                  setSecretEditing(null);
                  setSecretValue('');
                }}
                onChange={v => setSecretValue(v)}
                onSubmit={submitSecret}
                t={t}
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.sentry_dsn')}>
              <SecretField
                isSet={lc.sentry_dsn_set}
                isEditing={secretEditing === 'sentry_dsn'}
                value={secretEditing === 'sentry_dsn' ? secretValue : ''}
                submitting={secretSubmitting}
                error={secretEditing === 'sentry_dsn' ? secretError : null}
                disabled={readOnly}
                onStartEdit={() => {
                  setSecretEditing('sentry_dsn');
                  setSecretValue('');
                  setSecretError(null);
                }}
                onCancelEdit={() => {
                  setSecretEditing(null);
                  setSecretValue('');
                }}
                onChange={v => setSecretValue(v)}
                onSubmit={submitSecret}
                t={t}
              />
            </GitlabField>
            <GitlabField label={t('admin.lc.detail.gitlabConfig.field.telegram_alert_token')}>
              <SecretField
                isSet={lc.telegram_alert_token_set}
                isEditing={secretEditing === 'telegram_alert_token'}
                value={secretEditing === 'telegram_alert_token' ? secretValue : ''}
                submitting={secretSubmitting}
                error={secretEditing === 'telegram_alert_token' ? secretError : null}
                disabled={readOnly}
                onStartEdit={() => {
                  setSecretEditing('telegram_alert_token');
                  setSecretValue('');
                  setSecretError(null);
                }}
                onCancelEdit={() => {
                  setSecretEditing(null);
                  setSecretValue('');
                }}
                onChange={v => setSecretValue(v)}
                onSubmit={submitSecret}
                t={t}
              />
            </GitlabField>
          </>
        )}
      </GitlabFormGroup>
    </div>
  );
}

/** Sub-section wrapper with a labelled heading + 2-col grid for its
 *  fields. Used inside GitlabConfigSection to group fields by their
 *  GitLab variable category (SSH / runtime / secrets). */
function GitlabFormGroup({
  title, hint, children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2">
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h4>
        {hint && (
          <span className="text-[10px] text-muted-foreground/80">{hint}</span>
        )}
      </div>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
        {children}
      </div>
    </div>
  );
}

function GitlabField({
  label, hint, children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="block select-none text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function SwitchRow({
  checked, onCheckedChange, disabled, label,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <div className="flex h-9 items-center gap-2">
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

/** Write-once secret input. Shows "✓ установлен" + a "Set new"
 *  button when isSet=true; switches to an inline password input on
 *  edit; never reveals the existing value (BE doesn't return it). */
function SecretField({
  isSet, isEditing, value, submitting, error, disabled,
  onStartEdit, onCancelEdit, onChange, onSubmit, t,
}: {
  isSet: boolean;
  isEditing: boolean;
  value: string;
  submitting: boolean;
  error: string | null;
  disabled?: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onChange: (v: string) => void;
  onSubmit: () => void;
  t: T;
}) {
  if (isEditing) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Input
            type="password"
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={t('admin.lc.detail.gitlabConfig.secret.placeholder')}
            autoFocus
            disabled={submitting}
          />
          <Button
            size="sm"
            variant="default"
            onClick={onSubmit}
            disabled={submitting || !value}
          >
            {submitting
              ? <Loader2 className="size-3.5 animate-spin" />
              : t('admin.lc.detail.gitlabConfig.secret.save')}
          </Button>
          <Button size="icon-sm" variant="ghost" onClick={onCancelEdit} disabled={submitting}>
            <X className="size-3.5" />
          </Button>
        </div>
        {error && <p className="text-[10px] text-destructive">{error}</p>}
      </div>
    );
  }
  return (
    <div className="flex h-9 items-center justify-between gap-2 rounded-md border border-input bg-background px-3">
      <span className="text-xs">
        {isSet
          ? <span className="text-emerald-700 dark:text-emerald-300">{t('admin.lc.detail.gitlabConfig.secret.set')}</span>
          : <span className="text-muted-foreground">{t('admin.lc.detail.gitlabConfig.secret.unset')}</span>}
      </span>
      <button
        type="button"
        onClick={onStartEdit}
        disabled={disabled}
        className="text-xs font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isSet
          ? t('admin.lc.detail.gitlabConfig.secret.replace')
          : t('admin.lc.detail.gitlabConfig.secret.setBtn')}
      </button>
    </div>
  );
}

// `GitlabPushErrorBanner` removed in the 2026-05-23 redesign. The
// create flow moved to Section 3, which owns its own error display
// for missing_fields / clash / generic errors. Section 1 only
// surfaces PATCH-level errors now (inline above the form).

/** Coarse relative-time formatter — minutes/hours/days. Used by
 *  the "last pushed N ago" label. */
function formatRelativeTime(iso: string, t: T): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ${t('admin.lc.detail.ago')}`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ${t('admin.lc.detail.ago')}`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ${t('admin.lc.detail.ago')}`;
  const d = Math.round(h / 24);
  return `${d}d ${t('admin.lc.detail.ago')}`;
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  // 4j fix per Alexey: pre-4j the heading was `text-xs` + no separator,
  // so the 16px space-y between heading and body felt cramped. Now:
  // bigger heading text (text-sm + foreground colour), an explicit
  // bottom-margin via `pb-2` (8px), and a thin separator line that
  // visually anchors the heading to its card. Net visual gap ≈ 24px,
  // with the separator making the structure unambiguous.
  return (
    <h2 className="border-b border-border/60 pb-2 text-sm font-semibold tracking-tight text-foreground">
      {children}
    </h2>
  );
}

function Field({
  label,
  value,
  mono = false,
  muted = false,
  editable = false,
  isEmpty = false,
  required = false,
  error,
  lockedHint,
  children,
  emptyHint,
}: {
  label: string;
  value?: string;
  mono?: boolean;
  muted?: boolean;
  editable?: boolean;
  /** When true + `editable`, render an empty-state placeholder hint
   *  beneath the InlineEditableText so the operator sees "click to
   *  fill" rather than a near-invisible empty pill. */
  isEmpty?: boolean;
  /** Renders a red asterisk next to the label. Surfaces "this field
   *  is required before the LC can advance" — used for fields the
   *  BE marks as required pre-setup (deployment_key + wg_ip +
   *  device_number per Alexey 2026-05-23 live test feedback). */
  required?: boolean;
  /** Per-field PATCH error string (e.g. BE 409 detail). Renders
   *  inline below the input + adds a red ring around the editable
   *  content so the operator sees the error next to the field that
   *  caused it rather than at the bottom of the card. */
  error?: string | null;
  lockedHint?: string;
  emptyHint?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="space-y-0.5">
      <div className="select-none text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
        {required && (
          <span className="ml-1 text-destructive" aria-label="required">*</span>
        )}
      </div>
      <div className={cn(error && 'rounded-md ring-1 ring-destructive/60')}>
        {editable && children
          ? children
          : (
            <div
              className={cn(
                'text-sm',
                mono && 'font-mono text-xs',
                muted && 'italic text-muted-foreground',
              )}
            >
              {value}
            </div>
          )}
      </div>
      {error && (
        <div className="text-[11px] font-medium text-destructive">{error}</div>
      )}
      {!error && editable && isEmpty && emptyHint && (
        <div className="text-[10px] italic text-muted-foreground">{emptyHint}</div>
      )}
      {lockedHint && (
        <div className="text-[10px] text-muted-foreground/80">{lockedHint}</div>
      )}
    </div>
  );
}

/**
 * Read-only field for jetson-originated values where the full string
 * matters (MAC, wg_pubkey, ssh_pubkey). Renders the value selectable
 * in a wrapping mono block + a copy button on hover. Spans both
 * grid columns so RSA pubkeys (~372 chars) wrap without forcing the
 * sibling grid cell into an unusable narrow column.
 */
function CopyableField({
  label,
  value,
  t,
  emptyLabel = '—',
}: {
  label: string;
  value: string | null | undefined;
  t: T;
  emptyLabel?: string;
}) {
  const [copied, setCopied] = useState(false);
  const isEmpty = !value;
  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };
  return (
    <div className="space-y-0.5 sm:col-span-2">
      <div className="select-none text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {isEmpty
        ? (
          <div className="text-sm italic text-muted-foreground">{emptyLabel}</div>
        )
        : (
          <div className="group relative">
            <div className="break-all rounded-md border bg-muted/20 px-2 py-1.5 pr-12 font-mono text-[11px] leading-snug text-foreground">
              {value}
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              className="absolute right-1 top-1 opacity-60 transition-opacity hover:opacity-100"
              onClick={copy}
              aria-label={t('common.copy')}
              title={copied ? t('common.copied') : t('common.copy')}
            >
              <Copy className="size-3.5" />
            </Button>
          </div>
        )}
    </div>
  );
}

// ── Discovery panel (collapsible, inside ParamsCard) ───────────────

function DiscoveryPanel({
  lcId,
  t,
  onActive,
}: {
  lcId: string;
  t: T;
  /** Fires true on expand, false on collapse. Lets the parent tighten
   *  its SWR poll while the operator's actively running the curl. */
  onActive: (active: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [tokenBusy, setTokenBusy] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  // Bumped to force a re-mint after the operator clicks "Try again"
  // on a previous timeout/error. The mint effect keys on this so
  // pressing the button kicks off a fresh attempt without us having
  // to lift the inflight ref manually.
  const [mintAttempt, setMintAttempt] = useState(0);

  // Single-fire latch for the mint effect. Pre-fix the effect
  // depended on `tokenBusy`; `setTokenBusy(true)` inside the effect
  // triggered a re-render which re-ran the effect, the cleanup set
  // `cancelled=true`, and the still-in-flight promise's `.finally`
  // bailed on `cancelled` → tokenBusy stuck on true forever (UI
  // stuck on "Выдаём токен…"). Per Alexey 2026-05-24 staging test.
  // Inflight ref + a 10s timeout fallback replace the buggy
  // cancellation dance.
  const inflightRef = useRef(false);

  // Propagate expand/collapse to the parent for SWR-cadence control.
  useEffect(() => {
    onActive(open);
    return () => onActive(false);
  }, [open, onActive]);

  // Per task #89: the detail-page Discovery panel uses a tokenized
  // curl so a refresh POST binds to THIS specific LC even if its
  // eth0_mac is empty. Mint the token on first expand (24h validity
  // matches the BE's enrollment-token TTL — operator has plenty of
  // time to paste-and-run). If minting fails (or 10s passes with no
  // response), the panel surfaces an inline error + "Try again"
  // button — no spinner-without-timeout per team-lead 2026-05-24.
  useEffect(() => {
    if (!open || token || inflightRef.current) return;
    inflightRef.current = true;
    setTokenBusy(true);
    setTokenError(null);

    const timeoutHandle = window.setTimeout(() => {
      // 10s fallback: if neither resolve nor reject fired (network
      // hang / proxy stall), surface a clear error so the operator
      // isn't stuck staring at the spinner.
      if (inflightRef.current) {
        setTokenError(t('admin.lc.detail.discoveryPanel.timeout'));
        setTokenBusy(false);
        inflightRef.current = false;
      }
    }, 10_000);

    reissueEnrollmentToken(lcId)
      .then(resp => { setToken(resp.data.token); })
      .catch(e => { setTokenError(extractApiError(e, t('error.general'))); })
      .finally(() => {
        window.clearTimeout(timeoutHandle);
        setTokenBusy(false);
        inflightRef.current = false;
      });
    // Note: NO cleanup that flips a cancelled flag. The effect is
    // single-fire-per-expand (gated by inflightRef + token), and
    // setState on an unmounted component is a tolerable warning
    // here (the panel lives for the LC's detail-page lifetime, no
    // realistic unmount-mid-flight).
  }, [open, token, lcId, t, mintAttempt]);

  const retryMint = () => {
    setToken(null);
    setTokenError(null);
    inflightRef.current = false;
    setMintAttempt(n => n + 1);
  };

  // Polish: gate the curl render on token-or-error so the operator
  // can't accidentally copy a tokenless curl during the mint window.
  // (Tokenless would still work via BE MAC-match — but it bypasses
  // the per-LC binding xxvii asked for; presenting it as the
  // "primary" curl misleads the operator.)
  const tokenReady = token != null;
  const tokenFailed = tokenError != null;
  const curl = `curl -fsSL ${getDiscoveryScriptUrl(token ?? undefined)} | sudo bash`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(curl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // non-HTTPS dev fallback — operator can select manually
    }
  };

  return (
    <div className="rounded-md border bg-muted/20">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-foreground hover:bg-muted/40"
        onClick={() => setOpen(o => !o)}
      >
        {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        {t('admin.lc.detail.discoveryPanel.title')}
      </button>
      {open && (
        <div className="space-y-2 px-3 pb-3 text-xs">
          <p className="text-muted-foreground">
            {t('admin.lc.detail.discoveryPanel.hint')}
          </p>
          {tokenBusy && !tokenReady && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              {t('admin.lc.detail.discoveryPanel.issuingToken')}
            </div>
          )}
          {tokenFailed && (
            <div className="flex items-start justify-between gap-2 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
              <span className="flex-1">{tokenError}</span>
              <button
                type="button"
                onClick={retryMint}
                className="shrink-0 font-medium underline hover:no-underline"
              >
                {t('admin.lc.detail.discoveryPanel.retry')}
              </button>
            </div>
          )}
          {(tokenReady || tokenFailed) && (
            <div className="overflow-hidden rounded-md border border-emerald-500/30 bg-emerald-500/5">
              {/* 4i polish: distinct emerald + Search icon header
                  strip so the operator can't confuse this re-runnable
                  refresh curl with the one-shot setup curl in
                  Section 3 (which uses primary/Rocket). */}
              <div className="flex items-center gap-1.5 border-b border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-800 dark:text-emerald-300">
                <Search className="size-3" />
                {t('admin.lc.detail.discoveryPanel.label')}
              </div>
              <div className="relative">
                <pre className="overflow-x-auto p-2 font-mono text-[11px] leading-relaxed text-foreground">
                  {curl}
                </pre>
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute right-2 top-2"
                  onClick={copy}
                >
                  <Copy className="size-3.5" />
                  {copied ? t('common.copied') : t('common.copy')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Section 2: Setup steps ─────────────────────────────────────────

/**
 * Unified Setup card (4j): merges what used to be SetupStepsCard
 * (Section 2 — the checklist) and NextStepCard (Section 3 — current-
 * step body) into a single card with one header. Pre-4j the two
 * cards could tell contradictory stories (e.g. checklist showed
 * "WG_CONFIGURED pending" while the body said "fill params above"
 * — the user reported this confusion). The merge + the
 * `requiredParamsFilled` prop threaded into `<SetupStageChecklist>`
 * (which now prepends a synthetic "Параметры заполнены" row when
 * params are missing) make the checklist and body agree on what
 * the current step actually is.
 *
 * Sits above ParamsCard. Disappears entirely when DOCKER_RUNNING
 * — at that point the LC is a permanent record, only Params stay
 * visible.
 */
function SetupCard({
  lc,
  lastSetupError,
  requiredParamsFilled,
  setupCurl,
  setupCurlBusy,
  setupCurlError,
  setupCurlMintedAt,
  onIssueCurl,
  onChanged,
  t,
}: {
  lc: LocalComputer;
  lastSetupError: SetupError | null;
  requiredParamsFilled: boolean;
  setupCurl: string | null;
  setupCurlBusy: boolean;
  setupCurlError: string | null;
  setupCurlMintedAt: number | null;
  onIssueCurl: () => void | Promise<void>;
  /** SWR refetch — bumped after a match-or-create returns so the
   *  detail page picks up the new setup_stage + gitlab_locked_at. */
  onChanged: () => Promise<void>;
  t: T;
}) {
  const { status: beStatus, nextStage } = computeNextRowState(lc, lastSetupError);

  // 4i polish carried forward: when the setup curl first appears,
  // scroll the card into view so the operator notices without
  // re-scanning the whole page. Guarded by `lastFiredFor` ref so
  // SWR revalidations don't re-scroll.
  const cardRef = useRef<HTMLDivElement | null>(null);
  const lastFiredFor = useRef<string | null>(null);
  useEffect(() => {
    if (!cardRef.current || !setupCurl) return;
    const key = `${lc.id}:${setupCurlMintedAt ?? 0}`;
    if (lastFiredFor.current === key) return;
    lastFiredFor.current = key;
    cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [lc.id, setupCurl, setupCurlMintedAt]);

  // Effective current-step status. Params-missing trumps the BE
  // status — the params row is the actual current attention target,
  // not the next BE stage.
  const effectiveStatus: 'running' | 'failure' | 'pending' =
    !requiredParamsFilled ? 'pending' : beStatus === 'success' ? 'pending' : beStatus;

  return (
    <Card variant="section" ref={cardRef}>
      <CardContent className="space-y-5">
        <SectionHeading>{t('admin.lc.detail.sections.setup')}</SectionHeading>

        {/* Steps list — synthetic params row + BE stages */}
        <SetupStageChecklist
          lc={lc}
          lastSetupError={lastSetupError}
          requiredParamsFilled={requiredParamsFilled}
        />

        {/* Current-step body — mirrors the FIRST pending row above.
            When params are missing, that's the params row → "fill
            params" hint. Otherwise it's the next BE stage. */}
        <div className="space-y-2 border-t border-border/60 pt-4">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t('admin.lc.detail.sections.nextStep')}
          </div>
          {effectiveStatus === 'running' && nextStage && (
            <RunningBody nextStage={nextStage} t={t} />
          )}
          {effectiveStatus === 'failure' && lastSetupError && (
            <FailureBody lastSetupError={lastSetupError} t={t} />
          )}
          {effectiveStatus === 'pending' && (
            <PendingBody
              lc={lc}
              lcId={lc.id}
              nextStage={nextStage}
              requiredParamsFilled={requiredParamsFilled}
              setupCurl={setupCurl}
              setupCurlBusy={setupCurlBusy}
              setupCurlError={setupCurlError}
              setupCurlMintedAt={setupCurlMintedAt}
              onIssueCurl={onIssueCurl}
              onChanged={onChanged}
              t={t}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function RunningBody({ nextStage, t }: { nextStage: SetupStage; t: T }) {
  return (
    <div className="flex items-start gap-2 rounded-md bg-sky-50 px-3 py-2 text-sm text-sky-900 dark:bg-sky-950/30 dark:text-sky-100">
      <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin" />
      <span>
        {t('admin.lc.detail.nextStep.running', {
          stage: t(`admin.lc.stage.${nextStage}`),
        })}
      </span>
    </div>
  );
}

function FailureBody({
  lastSetupError,
  t,
}: {
  lastSetupError: SetupError;
  t: T;
}) {
  return (
    <div className="space-y-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
      <div className="font-medium">{t('admin.lc.detail.nextStep.failure')}</div>
      {lastSetupError.stage && (
        <div className="text-xs">
          {t('admin.lc.detail.nextStep.failureStage', {
            stage: t(`admin.lc.stage.${lastSetupError.stage}`),
          })}
        </div>
      )}
      {lastSetupError.failed_cmd && (
        <pre className="overflow-x-auto rounded-sm bg-background/50 p-2 font-mono text-[11px] leading-relaxed text-foreground">
          {lastSetupError.failed_cmd}
        </pre>
      )}
      {lastSetupError.exit_code != null && (
        <div className="text-[11px] opacity-80">
          {t('admin.lc.detail.nextStep.exitCode', {
            code: String(lastSetupError.exit_code),
          })}
        </div>
      )}
      <p className="text-xs">{t('admin.lc.detail.nextStep.failureHint')}</p>
    </div>
  );
}

/** Stages the BE doesn't auto-advance (yet). Section 3 surfaces a
 *  "waiting" hint for each — operator does the external work; the
 *  BE will detect it once auto-advance lands (flagged to team-lead
 *  2026-05-23 — no manual progression on FE per Alexey). */
const MANUAL_ADVANCE_STAGES = new Set<SetupStage>([
  'GITLAB_REGISTERED',
  'TRAEFIK_REGISTERED',
  'DOCKER_RUNNING',
]);

/** Section-3 amber warning when the discovered LC's hostname looks
 *  like a factory default (`linza-desktop` is what the jetsons ship
 *  with, multiple jetsons collide on it) AND display_name still
 *  matches it verbatim. Surfaces while the operator is on the
 *  params-fill step so they pick a meaningful display_name BEFORE
 *  binding. Per Alexey 2026-05-23 live test (bug #4). */
function HostnameClashWarning({ lc, t }: { lc: LocalComputer; t: T }) {
  const hostnameLooksDefault =
    typeof lc.hostname === 'string' && lc.hostname.startsWith('linza-');
  const displayNameStillDefault = lc.display_name === lc.hostname;
  if (!hostnameLooksDefault || !displayNameStillDefault) return null;
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
      {t('admin.lc.detail.nextStep.hostnameClashWarning', { hostname: lc.hostname })}
    </div>
  );
}

function PendingBody({
  lc,
  lcId,
  nextStage,
  requiredParamsFilled,
  setupCurl,
  setupCurlBusy,
  setupCurlError,
  setupCurlMintedAt,
  onIssueCurl,
  onChanged,
  t,
}: {
  /** Full LC for the hostname-clash warning detection — discovery
   *  POSTs `linza-desktop` (jetson factory hostname) verbatim, which
   *  collides with the display_name default. The warning surfaces
   *  while the operator's still on the params-fill step so they
   *  pick a meaningful display_name before binding. */
  lc: LocalComputer;
  lcId: string;
  nextStage: SetupStage | null;
  requiredParamsFilled: boolean;
  setupCurl: string | null;
  setupCurlBusy: boolean;
  setupCurlError: string | null;
  setupCurlMintedAt: number | null;
  onIssueCurl: () => void | Promise<void>;
  onChanged: () => Promise<void>;
  t: T;
}) {
  // Order matters: when the LC has progressed past the param-collection
  // stages (i.e. we're at a manual stage), the BE knows params were
  // OK earlier — don't re-prompt "fill params" even if a field appears
  // empty (operator can clear values without rolling setup_stage back).
  if (nextStage && MANUAL_ADVANCE_STAGES.has(nextStage)) {
    return (
      <ManualStageWaiting
        lcId={lcId}
        nextStage={nextStage}
        onChanged={onChanged}
        t={t}
      />
    );
  }
  if (!requiredParamsFilled) {
    return (
      <div className="space-y-2.5">
        <p className="text-sm text-foreground">
          {t('admin.lc.detail.nextStep.fillParamsHint')}
        </p>
        <HostnameClashWarning lc={lc} t={t} />
      </div>
    );
  }
  return (
    <SetupCurlBody
      setupCurl={setupCurl}
      setupCurlBusy={setupCurlBusy}
      setupCurlError={setupCurlError}
      setupCurlMintedAt={setupCurlMintedAt}
      onIssueCurl={onIssueCurl}
      t={t}
    />
  );
}

/** 4i polish: tokens expire 24h after mint. If the operator's been
 *  sitting on the page > STALE_AFTER_HOURS, silently re-mint on the
 *  next copy click so the pasted curl doesn't die mid-run. 23h leaves
 *  a 1h buffer for paste-and-run latency on the jetson side. */
const TOKEN_STALE_AFTER_HOURS = 23;

function isTokenStale(mintedAt: number | null): boolean {
  if (mintedAt == null) return false;
  return Date.now() - mintedAt > TOKEN_STALE_AFTER_HOURS * 3600 * 1000;
}

function SetupCurlBody({
  setupCurl,
  setupCurlBusy,
  setupCurlError,
  setupCurlMintedAt,
  onIssueCurl,
  t,
}: {
  setupCurl: string | null;
  setupCurlBusy: boolean;
  setupCurlError: string | null;
  setupCurlMintedAt: number | null;
  onIssueCurl: () => void | Promise<void>;
  t: T;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    // Stale-token guard: if the token's near-expiry, re-mint before
    // copy so the curl the operator pastes is fresh. Falls through
    // to copy-of-current if the re-mint fails (operator gets a curl
    // that probably still works for a few more minutes).
    if (isTokenStale(setupCurlMintedAt)) {
      await onIssueCurl();
      // `setupCurl` is captured from the parent render — the
      // re-mint just bumped state but we still have the old value
      // here. Skip the clipboard write this round; the next render
      // re-issues with the fresh curl, operator clicks again.
      return;
    }
    if (!setupCurl) return;
    try {
      await navigator.clipboard.writeText(setupCurl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };
  return (
    <div className="space-y-2">
      <p className="text-sm text-foreground">
        {t('admin.lc.detail.nextStep.runSetupHint')}
      </p>
      {setupCurlBusy && !setupCurl && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          {t('admin.lc.detail.nextStep.issuingToken')}
        </div>
      )}
      {setupCurlError && (
        <div className="space-y-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <div>{setupCurlError}</div>
          <Button variant="outline" size="sm" onClick={onIssueCurl}>
            {t('admin.lc.detail.nextStep.retryIssue')}
          </Button>
        </div>
      )}
      {setupCurl && (
        <div className="overflow-hidden rounded-md border border-primary/30 bg-primary/5">
          {/* 4i polish: header strip differentiates the setup curl
              (one-shot, runs the full setup) from the discovery curl
              in Section 1 (re-runnable, refreshes jetson params).
              Distinct colour + icon makes them unmistakable when the
              operator switches between the two. */}
          <div className="flex items-center gap-1.5 border-b border-primary/30 bg-primary/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary">
            <Rocket className="size-3" />
            {t('admin.lc.detail.nextStep.setupCurlLabel')}
            {isTokenStale(setupCurlMintedAt) && (
              <span className="ml-2 normal-case tracking-normal text-amber-700 dark:text-amber-300">
                · {t('admin.lc.detail.nextStep.tokenStaleHint')}
              </span>
            )}
          </div>
          <div className="relative">
            <pre className="overflow-x-auto p-2 font-mono text-[11px] leading-relaxed text-foreground">
              {setupCurl}
            </pre>
            <Button
              variant="outline"
              size="sm"
              className="absolute right-2 top-2"
              onClick={copy}
            >
              {isTokenStale(setupCurlMintedAt) && !setupCurlBusy
                ? <RefreshCw className="size-3.5" />
                : <Copy className="size-3.5" />}
              {copied
                ? t('common.copied')
                : isTokenStale(setupCurlMintedAt)
                  ? t('admin.lc.detail.nextStep.refreshToken')
                  : t('common.copy')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Stages whose advance depends on a deploy-pipeline run in GitLab —
// the BE auto-detect probe sees the resulting Environment / running
// container only after the pipeline lands. Surface a button so the
// operator can trigger the pipeline directly instead of waiting for
// the next scheduled run.
const STAGES_NEEDING_GITLAB_ACTION: ReadonlySet<SetupStage> = new Set([
  'GITLAB_REGISTERED',
  'DOCKER_RUNNING',
]);

// Project pipelines page. Hardcoded — the URL changes about once a
// decade; if we ever migrate GitLab instances we'd also have to
// touch services/plugins/gitlab_integration/tools/client.py (which
// has the same constant under another name). Move to a BE-served
// config endpoint then.
const GITLAB_PIPELINES_URL =
  'https://gitlab.dev.linzametrics.com/linzametrics/cctv-backend/-/pipelines';

function ManualStageWaiting({
  lcId,
  nextStage,
  onChanged,
  t,
}: {
  lcId: string;
  nextStage: SetupStage;
  onChanged: () => Promise<void>;
  t: T;
}) {
  // GITLAB_REGISTERED has its own match-or-create state machine
  // (team-lead 2026-05-23 redesign, task #99) — operator clicks
  // Continue, BE probes GitLab by host+port, matches or offers
  // create. The other manual stages (TRAEFIK_REGISTERED auto-
  // advances; DOCKER_RUNNING is pipeline-driven) keep the simple
  // description + Open-GitLab affordance.
  if (nextStage === 'GITLAB_REGISTERED') {
    return <GitlabMatchOrCreatePanel lcId={lcId} onChanged={onChanged} t={t} />;
  }

  const descKey = `admin.lc.detail.nextStep.manual.${nextStage}.desc`;
  const needsGitlabAction = STAGES_NEEDING_GITLAB_ACTION.has(nextStage);
  return (
    <div className="space-y-2.5">
      <p className="text-sm text-foreground">{t(descKey)}</p>
      {needsGitlabAction && (
        // Plain anchor styled as an outline button — the project's
        // <Button> component doesn't ship asChild.
        <a
          href={GITLAB_PIPELINES_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-8 items-center gap-2 rounded-md border border-input bg-background px-3 text-xs font-medium shadow-sm hover:bg-accent hover:text-accent-foreground"
        >
          <ExternalLink className="size-3.5" />
          {t('admin.lc.detail.nextStep.openGitlab')}
        </a>
      )}
    </div>
  );
}

// ── GitlabMatchOrCreatePanel ──────────────────────────────────────
//
// Section-3 body when the LC is waiting at REVERSE_SSH_LIVE →
// GITLAB_REGISTERED. State machine per team-lead 2026-05-23
// redesign + gitlab-pipeline's locked endpoint shapes
// (see api/localComputers.ts:MatchOrCreateResponse).
//
//   initial   → operator just landed; hint + [Продолжить] button.
//   matching  → POST /match-or-create/ with force_create=false in
//               flight; spinner.
//   no_match  → BE didn't find a host+port match. Show host+port +
//               [Проверить ещё раз] (retry match) + [Да, создать]
//               (escalate to force_create=true).
//   creating  → POST with force_create=true in flight; spinner.
//   clash     → uniqueness collision; show conflicts; operator goes
//               back to Section 1 to edit + clicks Retry.
//   error     → generic failure; [Повторить] retries last attempt.
//
// Successful `matched`/`created` outcomes don't render anything
// new — the BE has bumped setup_stage + populated gitlab_locked_at,
// so the parent SWR refetch flips Section 3 to the next step.

type GitlabMatchState =
  | { kind: 'initial' }
  | { kind: 'matching' }
  | { kind: 'no_match'; host: string; port: number }
  | { kind: 'creating' }
  | { kind: 'clash'; conflicts: GitlabClash[] }
  | { kind: 'error'; detail: string };

function GitlabMatchOrCreatePanel({
  lcId,
  onChanged,
  t,
}: {
  lcId: string;
  onChanged: () => Promise<void>;
  t: T;
}) {
  const [state, setState] = useState<GitlabMatchState>({ kind: 'initial' });

  // Inflight guard for the create button — once the operator commits
  // (force_create=true), refuse double-fire even if they spam.
  const inflightRef = useRef(false);

  const callMatchOrCreate = async (forceCreate: boolean) => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    setState({ kind: forceCreate ? 'creating' : 'matching' });
    try {
      const { data } = await matchOrCreateOnGitlab(lcId, forceCreate);
      if (data.action === 'matched' || data.action === 'created') {
        // Page refetch will rerender Section 3 with the next step —
        // we don't need to render anything else here.
        await onChanged();
        return;
      }
      if (data.action === 'no_match') {
        setState({
          kind: 'no_match',
          host: data.searched_host,
          port: data.searched_port,
        });
        return;
      }
      if (data.action === 'clash') {
        setState({ kind: 'clash', conflicts: data.conflicts });
        return;
      }
      // Forward-compat: unknown action.
      setState({ kind: 'error', detail: `unknown action: ${(data as { action: string }).action}` });
    } catch (e) {
      // Axios error path. BE returns 409 for both "lc_already_locked"
      // and "clash" — we read response.data to disambiguate.
      if (isAxiosError(e) && e.response?.data) {
        const body = e.response.data as { action?: string; conflicts?: GitlabClash[]; detail?: string };
        if (body.action === 'clash' && body.conflicts) {
          setState({ kind: 'clash', conflicts: body.conflicts });
        } else {
          setState({
            kind: 'error',
            detail: body.detail ?? extractApiError(e, t('error.general')),
          });
        }
      } else {
        setState({ kind: 'error', detail: t('error.general') });
      }
    } finally {
      inflightRef.current = false;
    }
  };

  if (state.kind === 'initial') {
    return (
      <div className="space-y-2.5">
        <p className="text-sm text-foreground">
          {t('admin.lc.detail.gitlabMatch.initialHint')}
        </p>
        <Button size="sm" onClick={() => callMatchOrCreate(false)}>
          {t('admin.lc.detail.gitlabMatch.continue')}
        </Button>
      </div>
    );
  }

  if (state.kind === 'matching') {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        {t('admin.lc.detail.gitlabMatch.checking')}
      </div>
    );
  }

  if (state.kind === 'creating') {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        {t('admin.lc.detail.gitlabMatch.creating')}
      </div>
    );
  }

  if (state.kind === 'no_match') {
    return (
      <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-900/60 dark:bg-amber-950/40">
        <p className="text-amber-900 dark:text-amber-200">
          {t('admin.lc.detail.gitlabMatch.noMatch', {
            host: state.host,
            port: state.port,
          })}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => callMatchOrCreate(false)}>
            {t('admin.lc.detail.gitlabMatch.recheck')}
          </Button>
          <Button size="sm" onClick={() => callMatchOrCreate(true)}>
            {t('admin.lc.detail.gitlabMatch.createNew')}
          </Button>
        </div>
      </div>
    );
  }

  if (state.kind === 'clash') {
    return (
      <div className="space-y-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        <div className="font-medium">
          {t('admin.lc.detail.gitlabMatch.clashTitle')}
        </div>
        <ul className="space-y-1 text-xs">
          {state.conflicts.map((c, i) => (
            <li key={`${c.field}:${c.reason}:${i}`}>
              · <span className="font-mono">{c.field}</span>{' '}
              {clashReasonText(c, t)}
            </li>
          ))}
        </ul>
        <p className="text-xs">{t('admin.lc.detail.gitlabMatch.clashHint')}</p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setState({ kind: 'initial' })}
        >
          {t('admin.lc.detail.gitlabMatch.backToContinue')}
        </Button>
      </div>
    );
  }

  // state.kind === 'error'
  return (
    <div className="space-y-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
      <p>{state.detail}</p>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setState({ kind: 'initial' })}
      >
        {t('admin.lc.detail.gitlabMatch.retry')}
      </Button>
    </div>
  );
}

/** Render a clash row's human-readable explanation based on the
 *  BE's `reason` discriminator + reason-specific extra fields. */
function clashReasonText(c: GitlabClash, t: T): string {
  if (c.reason === 'duplicate_in_local_computers_table') {
    return t('admin.lc.detail.gitlabMatch.clash.duplicateLc', {
      lcName: c.existing_lc_display_name ?? c.existing_lc_id ?? '?',
    });
  }
  if (c.reason === 'environment_already_exists_in_gitlab') {
    return t('admin.lc.detail.gitlabMatch.clash.envExists', {
      envName: c.existing_env_name ?? '?',
    });
  }
  if (c.reason === 'group_variable_already_exists_in_gitlab') {
    return t('admin.lc.detail.gitlabMatch.clash.varExists', {
      varKey: c.existing_var_key ?? '?',
    });
  }
  // Forward-compat: unknown reason — render raw.
  return c.reason;
}

// ── ZoneBindSection (#14) ─────────────────────────────────────────
//
// Bind/unbind zones to this LC. Closes the manual-Django-shell step
// from the Riders Lodge onboarding rollup (#29 step 21). Section
// renders nothing useful until the LC has a deployment_key set;
// before that the operator sees a "set deployment_key first" hint.

function ZoneBindSection({
  lc,
  t,
}: {
  lc: LocalComputer;
  t: T;
}) {
  const [data, setData] = useState<LcZonesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [pickerZoneId, setPickerZoneId] = useState<string>('');
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const { data } = await getLcZones(lc.id);
      setData(data);
    } catch (e) {
      setError(extractApiError(e, t('error.general')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [lc.id]);

  const onBind = async (zoneId: number) => {
    setBusy(zoneId);
    setError(null);
    try {
      await bindLcZone(lc.id, zoneId);
      setPickerZoneId('');
      await refresh();
    } catch (e) {
      setError(extractApiError(e, t('error.general')));
    } finally {
      setBusy(null);
    }
  };

  const onUnbind = async (zoneId: number) => {
    setBusy(zoneId);
    setError(null);
    try {
      await unbindLcZone(lc.id, zoneId);
      await refresh();
    } catch (e) {
      setError(extractApiError(e, t('error.general')));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3 border-t border-border/60 pt-4">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold tracking-tight">
          {t('admin.lc.detail.zoneBind.title')}
        </h3>
      </div>

      {!lc.deployment_key && (
        <p className="text-xs text-muted-foreground">
          {t('admin.lc.detail.zoneBind.noKeyHint')}
        </p>
      )}

      {loading && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          {t('common.loading')}
        </div>
      )}

      {error && (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}

      {data && lc.deployment_key && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {data.bound.length === 0 && (
              <span className="text-xs text-muted-foreground">
                {t('admin.lc.detail.zoneBind.noBound')}
              </span>
            )}
            {data.bound.map(z => (
              <ZoneChip
                key={z.id}
                zone={z}
                onUnbind={() => onUnbind(z.id)}
                busy={busy === z.id}
                t={t}
              />
            ))}
          </div>

          {data.candidates.length > 0 && (
            <div className="flex items-center gap-2">
              <Select
                value={pickerZoneId}
                onChange={e => setPickerZoneId(e.target.value)}
                className="h-8 text-xs"
              >
                <option value="">
                  {t('admin.lc.detail.zoneBind.pickerPlaceholder')}
                </option>
                {data.candidates.map(z => (
                  <option key={z.id} value={String(z.id)}>
                    {z.humanreadable_name}
                  </option>
                ))}
              </Select>
              <Button
                size="sm"
                variant="outline"
                disabled={!pickerZoneId || busy !== null}
                onClick={() => onBind(Number(pickerZoneId))}
              >
                {t('admin.lc.detail.zoneBind.bindButton')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ZoneChip({
  zone,
  onUnbind,
  busy,
  t,
}: {
  zone: ZoneBindSummary;
  onUnbind: () => void;
  busy: boolean;
  t: T;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/50 px-2 py-1 text-xs">
      <span className="font-medium">{zone.humanreadable_name}</span>
      <button
        type="button"
        onClick={onUnbind}
        disabled={busy}
        className="text-muted-foreground transition hover:text-destructive disabled:opacity-60"
        aria-label={t('admin.lc.detail.zoneBind.unbindAria')}
      >
        {busy ? <Loader2 className="size-3 animate-spin" /> : <X className="size-3" />}
      </button>
    </span>
  );
}
